<script>
import CustomTooltip from '@/components/booking/bookingList/custom-tooltip.vue'
import CandidateImage from '@/components/candidate/candidate-image.vue'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'
import ClientBookingStatus from '@/shared/constants/booking/ClientBookingStatus'

export default {
  name: 'BookingDetailHeader',
  components: {
    CandidateImage,
    CustomTooltip,
  },
  props: {
    booking: { type: Object, default: null },
    blocks: { type: Array, required: true, default: () => [] },
    groupMode: { type: Boolean, default: false },
    display: { type: Function, required: true },
  },
  data() {
    return {
      someDaysVary: false,
      headerEducatorsMax: 3,
      BookingStatusLabelType,
      ClientBookingStatus,
    }
  },
  computed: {
    // role/classification
    roleText() {
      return this.display(this.booking?.classification?.name)
    },

    locationText() {
      const parts = [
        this.booking?.client?.name,
        this.booking?.location?.name,
      ].filter(Boolean)

      return parts.length > 0 ? parts.join(', ') : '-'
    },

    // header title date range
    titleText() {
      if (!this.blocks.length) return '-'
      if (this.groupMode) {
        const first = this.blocks[0]
        const last = this.blocks[this.blocks.length - 1]
        return this._formatDateRangeFancy(first.start, last.end)
      } else {
        const start = this.booking.startDateTimeLocal
          ? new Date(this.booking.startDateTimeLocal)
          : null

        return start
          ? `${this._weekday(start)}, ${this._day(start)} ${this._monthYear(
              start
            )}`
          : '-'
      }
    },

    // header time range
    timeText() {
      if (!this.blocks.length) return '-'
      const first = this.blocks[0]
      const last = this.blocks[this.blocks.length - 1]
      return `${this._formatHourWithMinutes(
        first.start
      )} – ${this._formatHourWithMinutes(last.end)}`
    },

    // vary flag
    someDaysVaryComputed() {
      if (!this.groupMode || this.blocks.length <= 1) return false
      const norm = this.blocks.map((b) => ({
        sh: b.start.getHours(),
        sm: b.start.getMinutes(),
        eh: b.end.getHours(),
        em: b.end.getMinutes(),
      }))
      const first = JSON.stringify(norm[0])
      return norm.some((n) => JSON.stringify(n) !== first)
    },

    // Educator badges & counts (show avatar if first OR last exists)
    headerCandidates() {
      if (!this.booking) return []

      if (!this.groupMode) {
        const enrichedCandidate = this.enrichCandidate(this.booking.candidate)
        return enrichedCandidate ? [enrichedCandidate] : []
      }

      return this.extractUniqueCandidatesFromBlocks
    },
    extractUniqueCandidatesFromBlocks() {
      const { Pending, Open } = this.BookingStatusLabelType
      const uniqueCandidatesMap = new Map()

      for (const block of this.blocks) {
        const isAssignedStatus =
          block.bookingStatus !== Pending && block.bookingStatus !== Open

        if (!isAssignedStatus) continue

        const candidate = block.candidate
        const enrichedCandidate = this.enrichCandidate(candidate)
        const isValidCandidate =
          enrichedCandidate && !uniqueCandidatesMap.has(enrichedCandidate.id)

        if (isValidCandidate) {
          uniqueCandidatesMap.set(enrichedCandidate.id, enrichedCandidate)
        }
      }

      return Array.from(uniqueCandidatesMap.values())
    },
    headerEducatorsCount() {
      return this.headerCandidates.length
    },
    maxBadges() {
      return this.headerEducatorsMax
    },
    visibleEducatorsComputed() {
      const arr = this.headerCandidates
      return arr.length <= this.maxBadges ? arr : arr.slice(-this.maxBadges)
    },
    noEducatorsPlaced() {
      return isNullOrEmptyArray(this.headerCandidates)
    },
    // unique candidates minus actually shown badges
    extraCountComputed() {
      return Math.max(
        0,
        this.headerEducatorsCount - this.visibleEducatorsComputed.length
      )
    },
  },
  watch: {
    someDaysVaryComputed: {
      immediate: true,
      handler(v) {
        this.someDaysVary = !!v
      },
    },
  },
  methods: {
    // format helpers
    _weekday(d) {
      return new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(d)
    },
    _day(d) {
      return new Intl.DateTimeFormat(undefined, { day: 'numeric' }).format(d)
    },
    _monthYear(d) {
      return new Intl.DateTimeFormat(undefined, {
        month: 'long',
        year: 'numeric',
      }).format(d)
    },
    _month(d) {
      return new Intl.DateTimeFormat(undefined, { month: 'long' }).format(d)
    },

    // Time
    _formatHourWithMinutes(d) {
      if (!d) return '-'
      const h = d.getHours()
      const m = d
        .getMinutes()
        .toString()
        .padStart(2, '0')
      return `${h}:${m}`
    },

    // date range"
    _formatDateRangeFancy(start, end) {
      if (!start || !end) return '-'
      const sameMonth =
        start.getMonth() === end.getMonth() &&
        start.getFullYear() === end.getFullYear()
      const sameYear = start.getFullYear() === end.getFullYear()

      const left = `${this._weekday(start)}, ${this._day(start)}`
      if (sameMonth) {
        return `${left} – ${this._weekday(end)}, ${this._day(
          end
        )} ${this._monthYear(end)}`
      } else if (sameYear) {
        return `${left} ${this._month(start)} – ${this._weekday(
          end
        )}, ${this._day(end)} ${this._monthYear(end)}`
      }
      return `${left} ${this._monthYear(start)} – ${this._weekday(
        end
      )}, ${this._day(end)} ${this._monthYear(end)}`
    },
    enrichCandidate(candidate) {
      if (!candidate || candidate.id == null) return null

      const firstName = (candidate.firstName || '').trim()
      const lastName = (candidate.lastName || '').trim()
      const hasAnyName = !!(firstName || lastName)
      const fullName = hasAnyName
        ? `${firstName}${firstName && lastName ? ' ' : ''}${lastName}`
        : ''

      return {
        id: candidate.id,
        firstName,
        lastName,
        fullName,
        hasAnyName,
      }
    },
  },
}
</script>

<template>
  <div class="panel-header booking-details-panel">
    <div class="panel-header__left">
      <div class="panel-title">{{ titleText }}</div>

      <div class="meta-line">
        <v-icon size="16" class="meta-icon">mdi-map-marker-outline</v-icon>
        <span class="meta-text">{{ locationText }}</span>
      </div>

      <div class="meta-line">
        <v-icon size="16" class="meta-icon">mdi-lifebuoy</v-icon>
        <span class="meta-text">{{ roleText }}</span>
      </div>

      <div class="meta-line">
        <v-icon size="16" class="meta-icon">mdi-clock-time-four-outline</v-icon>
        <span class="meta-text meta-text--strong">{{ timeText }}</span>
        <span v-if="someDaysVary" class="meta-text meta-text--warn ml-1"
          >(Some days vary)</span
        >
      </div>

      <div class="meta-line">
        <v-icon size="16" class="meta-icon">mdi-account-outline</v-icon>
        <template v-if="noEducatorsPlaced">
          <span
            v-if="
              blocks.some(
                ({ status }) => status === ClientBookingStatus.multiTentative
              )
            "
            class="meta-text educator-label"
            >{{ $t('booking.multipleEducators') }}</span
          >
          <span v-else class="meta-text educator-label">{{
            $t('general.noEducator')
          }}</span>
        </template>
        <template v-else>
          <!-- badges (avatar shows if first OR last exists) -->
          <span
            v-if="headerCandidates.length > 0"
            class="initial-group ml-2 mr-2"
          >
            <span
              v-for="(candidate, i) in visibleEducatorsComputed"
              :key="`badge-${i}-${candidate.fullName}`"
              class="initial-badge"
              :style="{ zIndex: String(i + 2) }"
            >
              <CustomTooltip :text="candidate.fullName">
                <CandidateImage
                  v-if="candidate"
                  :id="candidate.id"
                  :first-name="candidate.firstName"
                  :last-name="candidate.lastName"
                  size="sm"
                />
              </CustomTooltip>
            </span>

            <span
              v-if="extraCountComputed > 0"
              class="initial-badge initial-badge--counter"
              :style="{ zIndex: 1 }"
              :title="`${extraCountComputed} more`"
            >
              +{{ extraCountComputed }}
            </span>
          </span>

          <span class="meta-text educator-label">
            {{ headerEducatorsCount }} educator{{
              headerEducatorsCount > 1 ? 's' : ''
            }}
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/side-panel.scss';
</style>
